/**
 * SyproFlow — Tally Bridge Download Page
 * ========================================
 * Gated page (behind ProtectedRoute) for downloading the Tally Bridge .exe.
 * Includes setup instructions for both TallyPrime and Tally.ERP 9, Windows
 * SmartScreen guidance, and FAQ.
 */

import React, { useState } from 'react';
import {
  Download, Shield, Monitor, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle, ArrowLeft, ArrowRight, Settings, Wifi,
  HardDrive, RefreshCw, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (() => {
  if (typeof window !== 'undefined' && window.SYPROFLOW_CONFIG?.API_BASE_URL) {
    return window.SYPROFLOW_CONFIG.API_BASE_URL;
  }
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('syproflow_api_base');
    if (stored) return stored;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return 'http://localhost:8000';
})();

const BRIDGE_VERSION = '1.3.0';

export default function DownloadBridge() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  const handleDownload = async () => {
    setDownloading(true);
    setError('');

    try {
      const token = localStorage.getItem('syproflow_token');
      const res = await fetch(`${API_BASE}/download/bridge-url`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          setError('Session expired. Please log in again.');
        } else if (res.status === 503) {
          setError('Download is not available yet. Please try again later.');
        } else {
          setError('Download failed. Please try again.');
        }
        return;
      }

      const data = await res.json();
      // data.url is a relative path like /download/bridge-file?dl=<token>
      // Browser navigates to it directly — native download, no blob buffering
      window.location.href = `${API_BASE}${data.url}`;
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setDownloading(false);
    }
  };

  const faqs = [
    {
      q: 'Bridge says "Not connected to Tally"',
      a: 'Make sure Tally is open and running on the same computer. The Bridge connects to Tally on port 9000 (TallyPrime) or 9000 (Tally.ERP 9). Check that Tally\'s server is enabled — see the setup steps above.',
    },
    {
      q: 'I clicked "Push to Tally" but nothing happened',
      a: 'Right-click the Bridge icon in your system tray and check the status. Common causes: Tally is not running, the company is not open in Tally, or a firewall is blocking localhost communication. Restart both Tally and the Bridge, then try again.',
    },
    {
      q: 'Windows blocked the download or won\'t let me run it',
      a: 'Windows SmartScreen may warn about unrecognized apps. Click "More info" on the warning dialog, then click "Run anyway". The Bridge runs entirely on your computer and only talks to Tally on localhost.',
    },
    {
      q: 'Voucher won\'t balance / amount mismatch',
      a: 'The Bridge validates Dr = Cr before pushing. If there\'s a mismatch, go back to SyproFlow, check the extracted amounts, fix any incorrect values, and push again.',
    },
    {
      q: 'Which Tally versions are supported?',
      a: 'TallyPrime (Release 1.0 and above) and Tally.ERP 9 (Release 6.4 and above). Both must be set to act as Server on port 9000 — see the setup steps above. The Bridge auto-detects which version is running.',
    },
  ];

  return (
    <div
      className="min-h-screen bg-slate-50 py-12 px-4"
      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
    >
      <div className="max-w-2xl mx-auto">

        {/* Back link */}
        <button
          onClick={() => navigate('/app')}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6 transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to SyproFlow
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800 mb-1" style={{ letterSpacing: '-0.02em' }}>
            Tally Bridge
          </h1>
          <p className="text-sm text-slate-500">
            Push vouchers from SyproFlow directly into Tally — no copy-paste, no XML files.
          </p>
        </div>

        {/* Download Card */}
        <div
          className="bg-white rounded-2xl shadow-lg p-8 mb-6 text-center"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
        >
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: '#fff7ed' }}
          >
            <Monitor className="w-7 h-7" style={{ color: '#ea580c' }} />
          </div>

          <h2 className="text-lg font-semibold text-slate-800 mb-1">Download for Windows</h2>
          <p className="text-xs text-slate-400 mb-5">
            Version {BRIDGE_VERSION} &middot; Windows 10 / 11 &middot; ~30 MB
          </p>

          <button
            onClick={handleDownload}
            disabled={downloading}
            className={
              'inline-flex items-center gap-2.5 px-7 py-3 rounded-xl text-sm font-semibold text-white shadow-lg transition-all duration-200'
              + (downloading
                ? ' opacity-70 cursor-wait'
                : ' hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]')
            }
            style={{
              background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
              boxShadow: '0 4px 14px rgba(234, 88, 12, 0.3)',
            }}
            title={`Download Tally Bridge v${BRIDGE_VERSION}`}
          >
            {downloading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading...</>
              : <><Download className="w-4 h-4" /> Download SyproFlowTallyBridge.exe</>
            }
          </button>

          {error && (
            <p className="text-[11px] text-red-500 mt-3">{error}</p>
          )}
        </div>

        {/* Windows SmartScreen Warning */}
        <div
          className="rounded-2xl p-5 mb-6"
          style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
            <div>
              <h3 className="text-sm font-semibold text-amber-800 mb-1">
                Windows may show a warning
              </h3>
              <p className="text-xs text-amber-700 leading-relaxed">
                Since the Bridge is new, Windows SmartScreen may say "Windows protected your PC".
                This is normal for new software.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-amber-800">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-200 text-[10px] font-bold">1</span>
                  Click <span className="font-semibold">"More info"</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-amber-400" />
                <div className="flex items-center gap-2 text-xs text-amber-800">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-200 text-[10px] font-bold">2</span>
                  Click <span className="font-semibold">"Run anyway"</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Steps */}
        <div
          className="bg-white rounded-2xl shadow-lg p-8 mb-6"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
        >
          <h2 className="text-lg font-semibold text-slate-800 mb-5">Setup in 3 steps</h2>

          {/* Step 1 */}
          <div className="flex gap-4 mb-6">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ background: '#ea580c' }}
            >
              1
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Enable Tally's Gateway Server</h3>

              <div className="mt-2 rounded-xl p-4" style={{ background: '#f8fafc' }}>
                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                  <Settings className="w-3.5 h-3.5" style={{ color: '#ea580c' }} />
                  TallyPrime
                </p>
                <ol className="text-xs text-slate-600 leading-relaxed list-decimal list-inside space-y-1 ml-1">
                  <li>Open TallyPrime and load your company</li>
                  <li>Press <kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-[10px] font-mono">F1</kbd> (Help) &rarr; <span className="font-medium">Settings</span> &rarr; <span className="font-medium">Connectivity</span></li>
                  <li>Under Client/Server configuration, set <span className="font-medium">TallyPrime acts as</span>: <span className="font-semibold text-emerald-600">Server</span></li>
                  <li>Set port to <span className="font-mono text-[11px]">9000</span></li>
                  <li>Accept (<kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-[10px] font-mono">Ctrl+A</kbd>)</li>
                </ol>
              </div>

              <div className="mt-3 rounded-xl p-4" style={{ background: '#f8fafc' }}>
                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                  <Settings className="w-3.5 h-3.5" style={{ color: '#ea580c' }} />
                  Tally.ERP 9
                </p>
                <ol className="text-xs text-slate-600 leading-relaxed list-decimal list-inside space-y-1 ml-1">
                  <li>Open Tally.ERP 9 and load your company</li>
                  <li><span className="font-medium">Gateway of Tally</span> &rarr; <kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-[10px] font-mono">F12</kbd> (Configure) &rarr; <span className="font-medium">Advanced Configuration</span></li>
                  <li>Set <span className="font-medium">Tally.ERP 9 is acting as</span>: <span className="font-semibold text-emerald-600">Server</span></li>
                  <li>Set port to <span className="font-mono text-[11px]">9000</span></li>
                  <li>Accept — Tally.ERP 9 will ask to restart, click <span className="font-semibold">Yes</span></li>
                </ol>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4 mb-6">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ background: '#ea580c' }}
            >
              2
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Run the Bridge</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Double-click the downloaded file to start it.
                It runs quietly in your system tray (near the clock) — there's no separate window, just a small icon. Keep it running while you work in SyproFlow.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ background: '#ea580c' }}
            >
              3
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Push from SyproFlow</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Extract a document in SyproFlow, open the Tally export panel, and click
                {' '}<span className="font-semibold text-slate-700">"Push to Tally"</span>.
                The Bridge will validate the voucher and send it straight into your open Tally company.
              </p>
            </div>
          </div>
        </div>

        {/* Trust / Privacy Line */}
        <div className="flex items-center justify-center gap-2 mb-6 py-3">
          <HardDrive className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <p className="text-xs text-slate-500">
            Everything stays on your computer. The Bridge talks only to Tally on localhost — no data is sent to the cloud.
          </p>
        </div>

        {/* FAQ */}
        <div
          className="bg-white rounded-2xl shadow-lg p-8"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
        >
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Frequently Asked Questions</h2>

          <div className="space-y-1">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-slate-100 last:border-0">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between gap-3 py-3 text-left transition hover:bg-slate-50 rounded-lg px-2 -mx-2"
                >
                  <span className="text-sm text-slate-700 font-medium">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  }
                </button>
                {openFaq === i && (
                  <p className="text-xs text-slate-500 leading-relaxed pb-3 px-2">
                    {faq.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <Shield className="w-3.5 h-3.5 text-green-500" />
          <span>Tally Bridge v{BRIDGE_VERSION} by SyproOne Solutions Pvt Ltd</span>
        </div>

      </div>
    </div>
  );
}
